import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertTriangle, X } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────────
// SkillTagInput — comma/Enter-separated tag input with removable chips
//                 + suggested skill chips.
// ──────────────────────────────────────────────────────────────────────────────

const DEFAULT_SKILL_POOL = [
  // Engineering
  'React','TypeScript','JavaScript','Node.js','Python','Go','Rust','Java','C++','C#',
  'Next.js','Vue.js','Angular','Svelte','GraphQL','REST API','PostgreSQL','MySQL',
  'MongoDB','Redis','Docker','Kubernetes','AWS','GCP','Azure','Terraform','CI/CD',
  'Git','Linux','Microservices','System Design','Data Structures','Algorithms',
  'Machine Learning','Deep Learning','NLP','LLM','PyTorch','TensorFlow','Pandas',
  'SQL','Spark','Kafka','Elasticsearch','FastAPI','Django','Flask','Spring Boot',
  // Design
  'Figma','UI Design','UX Design','UX Research','Prototyping','Wireframing',
  'Design Systems','User Testing','Interaction Design','Motion Design','Sketch',
  'Adobe XD','Illustrator','Photoshop','Branding','Typography','Accessibility',
  // Product
  'Product Management','Product Strategy','Roadmapping','Agile','Scrum','Kanban',
  'OKRs','A/B Testing','Product Analytics','Growth Hacking','Customer Discovery',
  'Stakeholder Management','PRD Writing','Jira','Confluence','Notion',
  // Marketing
  'SEO','SEM','Content Marketing','Email Marketing','Social Media','Copywriting',
  'Growth Marketing','Brand Marketing','Performance Marketing','Google Analytics',
  'Meta Ads','LinkedIn Ads','HubSpot','Salesforce','CRM',
  // Business / Leadership
  'Business Development','Sales','Fundraising','Venture Capital','Financial Modelling',
  'Leadership','Team Building','Hiring','Mentoring','Public Speaking','Communication',
  'Entrepreneurship','Operations','Project Management','Data Analysis','Excel',
  'Tableau','Power BI','Looker',
  // Web / No-code
  'Webflow','Bubble','Framer','WordPress','Shopify','Wix','Zapier','Airtable',
];

export interface SkillTagInputProps {
  id: string;
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  error?: string | null;
  /** Override the suggestion pool (defaults to DEFAULT_SKILL_POOL) */
  suggestions?: string[];
  /** Hide the field label (section heading already shows it) */
  hideLabel?: boolean;
  /** `profile` = neutral chips, scroll area, Work-tab layout */
  variant?: 'default' | 'profile';
  panelClassName?: string;
}

const normalize = (raw: string): string => raw.trim().slice(0, 40);

const displayTag = (tag: string, maxLen = 36): string =>
  tag.length > maxLen ? `${tag.slice(0, maxLen - 1)}…` : tag;

const SkillTagInput: React.FC<SkillTagInputProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  maxTags = 20,
  error,
  suggestions = DEFAULT_SKILL_POOL,
  hideLabel = false,
  variant = 'default',
  panelClassName,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('profile.skillTag.defaultPlaceholder', 'e.g. React, TypeScript, SQL');
  const [draft, setDraft] = useState('');
  const isProfile = variant === 'profile';
  const atLimit = value.length >= maxTags;
  const overLimit = value.length > maxTags;
  const canAdd = !atLimit;

  // Suggestions: filter pool by query, exclude already-added tags
  const query = draft.trim().toLowerCase();
  const suggestedSkills = suggestions
    .filter(s => !value.some(v => v.toLowerCase() === s.toLowerCase()))
    .filter(s => query ? s.toLowerCase().includes(query) : true)
    .slice(0, query ? 15 : isProfile ? 10 : 12);

  const addSuggestion = (skill: string) => {
    if (!canAdd) return;
    if (!value.some(v => v.toLowerCase() === skill.toLowerCase())) {
      onChange([...value, skill]);
    }
    setDraft('');
  };
  const hasError = Boolean(error);

  const commit = (raw: string): void => {
    const cleaned = normalize(raw);
    if (!cleaned) return;
    if (value.length >= maxTags) return;
    if (value.some(v => v.toLowerCase() === cleaned.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, cleaned]);
    setDraft('');
  };

  const remove = (idx: number): void => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (!atLimit) commit(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      remove(value.length - 1);
    }
  };

  const chipClass = isProfile
    ? (idx: number) =>
        `ds-skill-tag-chip pr-1${idx >= maxTags ? ' is-over-limit' : ''}`
    : () => 'ds-chip ds-chip-brand pr-1.5';

  const tagsAndInput = (
    <>
      {value.map((tag, idx) => (
        <span
          key={`${tag}-${idx}`}
          className={chipClass(idx)}
          title={tag}
        >
          <span className="truncate max-w-[14rem]">{displayTag(tag)}</span>
          <button
            type="button"
            onClick={() => remove(idx)}
            aria-label={t('profile.skillTag.removeAria', 'Remove {{tag}}', { tag })}
            className="w-4 h-4 inline-flex items-center justify-center rounded-full transition-colors touch-manipulation"
          >
            <X className="w-2.5 h-2.5" aria-hidden />
          </button>
        </span>
      ))}
      <input
        id={id}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (draft) commit(draft); }}
        placeholder={
          value.length === 0
            ? resolvedPlaceholder
            : canAdd
              ? t('profile.skillTag.addMore', 'Add more…')
              : t('profile.skillTag.removeExtrasToAdd', 'Remove extras to add more')
        }
        className="flex-1 min-w-[120px] bg-transparent outline-none t-body px-1.5 py-1"
        aria-invalid={hasError || overLimit || undefined}
        aria-describedby={hasError ? `${id}-error` : overLimit ? `${id}-overlimit` : undefined}
      />
    </>
  );

  const footer = (
    <div className={isProfile ? 'ds-skill-tags-footer' : 'flex items-center justify-between mt-1.5 px-0.5'}>
      {hasError ? (
        <p id={`${id}-error`} role="alert" className="t-meta t-danger">{error}</p>
      ) : (
        <p className="t-meta">{t('profile.skillTag.enterOrCommaToAdd', 'Enter or comma to add')}</p>
      )}
      <p
        className={`t-meta t-num shrink-0 ${overLimit || atLimit ? 't-danger' : ''}`}
        aria-live="polite"
      >
        {value.length}/{maxTags}
      </p>
    </div>
  );

  const suggestionsBlock = suggestedSkills.length > 0 && canAdd && (
    <div className={isProfile ? 'ds-skill-tags-suggestions pt-2.5' : 'mt-2.5'}>
      <p className="t-meta mb-1.5">
        {query ? t('profile.skillTag.matchingSkills', 'Matching skills') : t('profile.skillTag.suggested', 'Suggested')}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {suggestedSkills.map(skill => (
          <button
            key={skill}
            type="button"
            onClick={() => addSuggestion(skill)}
            className={`ds-chip ds-chip-button${isProfile ? ' ds-skill-suggestion-chip' : ''}`}
          >
            {skill}
            <span className={isProfile ? 't-tag t-muted' : 't-tag t-brand'}>+</span>
          </button>
        ))}
      </div>
    </div>
  );

  if (isProfile) {
    return (
      <div className={`w-full ${panelClassName ?? ''}`}>
        {!hideLabel && <label htmlFor={id} className="t-label block mb-1.5">{label}</label>}
        {overLimit && (
          <p id={`${id}-overlimit`} className="ds-work-skills-overlimit" role="status">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
            <span>
              {t(
                'profile.skillTag.overLimitWarning',
                'You have {{count}} extra skill(s) (often from a resume import). Remove down to {{maxTags}} so matching and recommendations stay accurate.',
                { count: value.length - maxTags, maxTags },
              )}
            </span>
          </p>
        )}
        <div className="ds-skill-tags-scroll vscode-scrollbar">
          <div className="ds-skill-tags-input-row">{tagsAndInput}</div>
        </div>
        {footer}
        {suggestionsBlock}
      </div>
    );
  }

  return (
    <div className="w-full space-y-1.5">
      {!hideLabel && <label htmlFor={id} className="t-label block">{label}</label>}
      <div
        className="flex flex-wrap items-center gap-1.5 p-2 transition-all min-h-[52px] rounded-lg border"
        style={{
          background: 'var(--ds-bg-input)',
          borderColor: hasError ? 'var(--ds-danger)' : 'var(--ds-border)',
          boxShadow: 'var(--ds-shadow-card)',
        }}
        onFocusCapture={e => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = hasError ? 'var(--ds-danger)' : 'var(--ds-border-focus)';
          el.style.boxShadow = 'var(--ds-shadow-focus)';
        }}
        onBlurCapture={e => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            const el = e.currentTarget as HTMLElement;
            el.style.borderColor = hasError ? 'var(--ds-danger)' : 'var(--ds-border)';
            el.style.boxShadow = 'var(--ds-shadow-card)';
          }
        }}
      >
        {tagsAndInput}
      </div>
      {footer}
      {suggestionsBlock}
    </div>
  );
};

export default SkillTagInput;
